<template>
  <div v-if="Object.keys(dataTypes).length !== 0">
    <table class="table is-bordered is-striped is-narrow is-hoverable">
      <thead>
        <tr>
          <th :aria-label="$t('dataTypeAuthorizations.name-data')">
            {{ $t("dataTypeAuthorizations.name-data") }}
          </th>

          <th
            v-for="(column, key) in listColumnScope"
            :key="key"
            :aria-label="localizedColumnName(column)"
          >
            <div class="header-column">
              <b-checkbox
                :key="`col-${key}-${renderToken}`"
                :model-value="isColumnChecked(key)"
                :indeterminate="isColumnIndeterminate(key)"
                :disabled="isColumnDisabled(key)"
                :aria-label="localizedColumnName(column)"
                @update:model-value="handleColumnToggle(key, $event)"
              />
              <span>{{ localizedColumnName(column) }}</span>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="dataType in dataTypes"
          :key="dataType.id"
        >
          <td :aria-label="dataType.name">
            {{ dataType.name }}
          </td>

          <td
            v-for="(column, key) in listColumnScope"
            :key="`${dataType.id}-${key}`"
            :aria-label="`${dataType.id}-${key}`"
            :class="{ 'is-locked-by-hierarchy': isLockedByHierarchyForDatatype(dataType.id, key) }"
          >
            <b-checkbox
              :key="`${dataType.id}-${key}-${renderToken}`"
              :model-value="isCheckboxChecked(dataType.id, key)"
              :disabled="isCheckboxDisabled(dataType.id, key) || isDisable"
              :title="getCheckboxTitle(dataType.id, key)"
              :aria-label="String(isCheckboxChecked(dataType.id, key))"
              @update:model-value="handleLineToggle(dataType.id, key, $event)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { computed, ref } from "vue";
import { getLocalizedColumnName } from "@/composable/authorization/grantableInfos";
import {
  applyCheck,
  applyUncheck,
  isLockedByHierarchy,
  OPERATION_EXTRACTION,
} from "@/composable/authorization/useOperationTypeHierarchy";

/**
 * Tableau d'autorisations pour datatypes / référentiels SANS scope
 * ( pas de filtre par valeurs de référentiel , droits globaux ) .
 *
 * <p>Applique la hiérarchie métier ( ticket #521 - réponse Damien Maurice
 * 2026-05-25 ) déléguée au composable {@link useOperationTypeHierarchy}
 * sur les deux canaux de modification :
 * <ul>
 *   <li><b>case ligne</b> ( un datatype × une opération ) -
 *       {@link handleLineToggle} : applique
 *       {@code applyCheck} / {@code applyUncheck} sur le set du datatype
 *       concerné ;</li>
 *   <li><b>case header de colonne</b> ( select-all sur toutes les lignes
 *       pour cette opération ) - {@link handleColumnToggle} : applique
 *       la même logique sur chaque datatype , en respectant les verrous
 *       ( un datatype dont la case est verrouillée par la hiérarchie
 *       n'est pas modifié ) .</li>
 * </ul>
 *
 * <p><b>Architecture des données :</b> source de vérité unique =
 * {@code props.authorization} ( objet parent ) , lu via
 * {@link getOperationTypesSet} . Aucun {@code reactive} local mutable :
 * tout passe par l'émission {@code update:authorization} . Cela évite
 * les désynchronisations entre un état local et la prop parent qui
 * étaient la cause racine du bug #521 ( le state local muté ignorait
 * la hiérarchie alors que le parent l'aurait imposée à la sauvegarde ) .
 *
 * <p><b>Verrou déterministe :</b> les cases verrouillées par la
 * hiérarchie sont {@code disabled} + signalées CSS via
 * {@code .is-locked-by-hierarchy} . Si Buefy contourne {@code disabled}
 * ( versions / bugs ) , le handler refuse l'émission ET incrémente
 * {@code renderToken} pour forcer le re-mount du composant , qui
 * re-lit la source de vérité parent .
 */
export default {
  name: "AuthorizationsForNoAuthorization",
  props: {
    authorization: {
      type: Object,
      required: true,
    },
    dataTypes: {
      type: Object,
      required: true,
    },
    listColumnScope: {
      type: Object,
      required: true,
    },
    isDisable: {
      type: Boolean,
      default: false,
    },
    hasDependencies: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:authorization"],

  setup(props, { emit }) {
    // ─── Source de vérité : props.authorization ──────────────────────────
    // Le composant ne garde AUCUN état local mutable . Toute modification
    // passe par l'émission update:authorization vers le parent , qui
    // re-rend ensuite via la prop ( binding unidirectionnel pur ) .

    const getOperationTypesSet = (datatypeId: string): Set<string> => {
      const auth = props.authorization?.[datatypeId];
      // Format historique : peut être un array direct OU un wrapper [0]
      // ( cf {@code props.authorization?.[datatype]?.[0]} dans le code
      // précédent ) . On normalise les deux cas .
      const node = Array.isArray(auth) ? auth[0] : auth;
      const ops = node?.operationTypes ?? [];
      const set = new Set<string>(Array.isArray(ops) ? ops : []);
      // Extraction forcée par dépendance cross-datatype ( mécanisme
      // historique préservé : un datatype dont un autre dépend doit
      // toujours être lisible en extraction ) .
      if (props.hasDependencies.includes(datatypeId)) {
        set.add(OPERATION_EXTRACTION);
      }
      return set;
    };

    // ─── Force re-mount sur toggle refusé ────────────────────────────────
    const renderToken = ref(0);

    // ─── Émission vers le parent ────────────────────────────────────────
    // Reconstruit l'objet authorization avec le nouveau set d'opérations
    // pour le datatype concerné , les autres datatypes restant inchangés .
    const emitDatatypeChange = (datatypeId: string, newOps: Set<string>): void => {
      const authorization: any = { ...props.authorization };
      authorization[datatypeId] = { operationTypes: Array.from(newOps) };
      emit("update:authorization", authorization);
    };

    // ─── Émission multi-datatypes ( toggle colonne ) ────────────────────
    const emitMultipleDatatypeChanges = (
      changes: Array<{ datatypeId: string; newOps: Set<string> }>,
    ): void => {
      if (changes.length === 0) return;
      const authorization: any = { ...props.authorization };
      changes.forEach(({ datatypeId, newOps }) => {
        authorization[datatypeId] = { operationTypes: Array.from(newOps) };
      });
      emit("update:authorization", authorization);
    };

    // ─── État UI dérivé ──────────────────────────────────────────────────
    const isForcedByCrossDatatype = (datatypeId: string, key: string): boolean =>
      key === OPERATION_EXTRACTION && props.hasDependencies.includes(datatypeId);

    const isCheckboxChecked = (datatypeId: string, key: string): boolean => {
      if (isForcedByCrossDatatype(datatypeId, key)) return true;
      return getOperationTypesSet(datatypeId).has(key);
    };

    const isLockedByHierarchyForDatatype = (datatypeId: string, key: string): boolean =>
      isLockedByHierarchy(key, getOperationTypesSet(datatypeId));

    const isCheckboxDisabled = (datatypeId: string, key: string): boolean =>
      isForcedByCrossDatatype(datatypeId, key)
      || isLockedByHierarchyForDatatype(datatypeId, key);

    const getCheckboxTitle = (datatypeId: string, key: string): string => {
      if (isForcedByCrossDatatype(datatypeId, key)) {
        return "Cette autorisation est induite par des dépendances et ne peut être modifiée directement.";
      }
      if (isLockedByHierarchyForDatatype(datatypeId, key)) {
        return "Verrouillée par la hiérarchie ( décocher d'abord le droit racine ) .";
      }
      return "";
    };

    // ─── Handler case ligne ──────────────────────────────────────────────
    const handleLineToggle = (datatypeId: string, key: string, newValue: boolean): void => {
      const ops = getOperationTypesSet(datatypeId);
      const wasChecked = isCheckboxChecked(datatypeId, key);
      if (newValue === wasChecked) return;

      if (newValue) {
        emitDatatypeChange(datatypeId, applyCheck(key, ops));
        return;
      }
      if (isCheckboxDisabled(datatypeId, key) || props.isDisable) {
        renderToken.value++;
        return;
      }
      emitDatatypeChange(datatypeId, applyUncheck(key, ops));
    };

    // ─── Handler header colonne ( select-all ) ──────────────────────────
    // Cochage : applique applyCheck sur chaque datatype non disabled .
    // Décochage : applique applyUncheck sur chaque datatype non verrouillé
    // ( les datatypes verrouillés par hiérarchie restent inchangés ,
    // conformément à la règle métier ) .
    const handleColumnToggle = (key: string, checked: boolean): void => {
      if (props.isDisable) {
        renderToken.value++;
        return;
      }
      const changes: Array<{ datatypeId: string; newOps: Set<string> }> = [];
      Object.keys(props.dataTypes).forEach((datatypeId) => {
        if (isForcedByCrossDatatype(datatypeId, key)) return;
        const ops = getOperationTypesSet(datatypeId);
        if (checked) {
          if (!ops.has(key)) {
            changes.push({ datatypeId, newOps: applyCheck(key, ops) });
          }
        }
        else {
          if (ops.has(key) && !isLockedByHierarchy(key, ops)) {
            changes.push({ datatypeId, newOps: applyUncheck(key, ops) });
          }
        }
      });
      if (changes.length === 0) {
        renderToken.value++;
        return;
      }
      emitMultipleDatatypeChanges(changes);
    };

    // ─── État UI dérivé des colonnes ────────────────────────────────────
    const datatypeIds = computed(() => Object.keys(props.dataTypes));

    const isColumnDisabled = (key: string): boolean =>
      datatypeIds.value.every(datatypeId =>
        isForcedByCrossDatatype(datatypeId, key) || props.isDisable,
      );

    const isColumnChecked = (key: string): boolean =>
      datatypeIds.value.every((datatypeId) => {
        if (isForcedByCrossDatatype(datatypeId, key)) return true;
        return getOperationTypesSet(datatypeId).has(key);
      });

    const isColumnIndeterminate = (key: string): boolean => {
      const values = datatypeIds.value
        .filter(datatypeId => !isForcedByCrossDatatype(datatypeId, key))
        .map(datatypeId => getOperationTypesSet(datatypeId).has(key));
      return values.some(Boolean) && !values.every(Boolean);
    };

    return {
      renderToken,
      isCheckboxChecked,
      isCheckboxDisabled,
      isLockedByHierarchyForDatatype,
      getCheckboxTitle,
      handleLineToggle,
      handleColumnToggle,
      isColumnChecked,
      isColumnIndeterminate,
      isColumnDisabled,
      localizedColumnName: getLocalizedColumnName,
    };
  },
};
</script>

<style scoped>
.header-column {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.is-locked-by-hierarchy {
  opacity: 0.55;
  cursor: not-allowed;
}
.is-locked-by-hierarchy :deep(.b-checkbox) {
  cursor: not-allowed;
}
</style>
